import { lazy, Suspense } from "react"
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"
import { ActiveUserLayout } from "components/Layouts/ActiveUserLayout"
import { AdminUserLayout } from "components/Layouts/AdminUserLayout"
import { AuthorizedUserLayout } from "components/Layouts/AuthorizedUserLayout"
import { LoginPagesLayout } from "components/Layouts/LoginPagesLayout"
import { CategoryAccessLayout } from "components/Layouts/CategoryAccessLayout"
import { ProductCategories, ProductCategoriesLink } from "components/Navbar/types"
import { UserSettingsPage } from "pages/Users/UserSettingsPage"
import { UICustomization } from "pages/UICustomization"
import { LoginPage, LoginSsoCallback } from "pages/LoginPage"
import { ActivateAccount } from "pages/ActivateAccount"
import { ProductsPage } from "pages/Products/ProductsPage"
import { AlertProvider } from "providers/AlertProvider"
import { PageNotFound } from "pages/NotFoundPage.tsx"
import { Viewer3DProductsPage } from "pages/Viewer3D/Viewer3DProductsPage"
import PageLoader from "components/PageLoader"
import { typedEnv } from "./config/typedEnv"

// Route-level code splitting: these pages pull heavy dependencies (three.js/@gltf-transform,
// recharts, highlight.js, the Banuba player), so they are lazy-loaded to keep the entry chunk lean.
const GlassesEditorPage = lazy(() =>
  import("pages/Products/Glasses/GlassesEditorPage").then((m) => ({
    default: m.GlassesEditorPage,
  }))
)
const IntegrationInfo = lazy(() =>
  import("pages/IntegrationInfo").then((m) => ({ default: m.IntegrationInfo }))
)
const ProductPage = lazy(() =>
  import("pages/Products/ProductPage").then((m) => ({ default: m.ProductPage }))
)
const Viewer3DEditorPage = lazy(() =>
  import("pages/Viewer3D/Viewer3DEditorPage").then((m) => ({ default: m.Viewer3DEditorPage }))
)
const Viewer3DIntegrationPage = lazy(() =>
  import("pages/Viewer3D/Viewer3DIntegrationPage").then((m) => ({
    default: m.Viewer3DIntegrationPage,
  }))
)

const App = () => {
  return (
    <BrowserRouter
      future={{
        v7_relativeSplatPath: true,
        v7_startTransition: true,
      }}
    >
      <AlertProvider>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {typedEnv.MODE === "test" && (
              <Route
                path="/__e2e/clerk"
                element={<div data-testid="e2e-clerk-ready" className="sr-only" />}
              />
            )}
            <Route element={<AuthorizedUserLayout />}>
              <Route element={<ActiveUserLayout />}>
                <Route element={<AdminUserLayout />}>
                  <Route path="/ui-customization/edit" element={<UICustomization />} />
                </Route>
                <Route path="/ui-customization" element={<UICustomization />} />
                <Route path="/integration-info" element={<IntegrationInfo />} />
                <Route path="/3d-viewer" element={<Viewer3DProductsPage />} />
                <Route path="/3d-viewer/add" element={<Viewer3DEditorPage />} />
                <Route path="/3d-viewer/edit" element={<Viewer3DEditorPage />} />
                <Route path="/3d-viewer/integration" element={<Viewer3DIntegrationPage />} />
                <Route element={<CategoryAccessLayout />}>
                  <Route path="*" element={<PageNotFound />} />
                  <Route path="/" element={<Navigate to={ProductCategoriesLink.MAKEUP} />} />
                  <Route
                    path={ProductCategoriesLink.MAKEUP}
                    element={<ProductsPage category={ProductCategories.MAKEUP} />}
                  />
                  <Route path={`${ProductCategoriesLink.MAKEUP}/edit`} element={<ProductPage />} />
                  <Route
                    path={`${ProductCategoriesLink.MAKEUP}/add`}
                    element={<ProductPage add />}
                  />
                  <Route
                    path={ProductCategoriesLink.GLASSES}
                    element={<ProductsPage category={ProductCategories.GLASSES} />}
                  />
                  <Route
                    path={`${ProductCategoriesLink.GLASSES}/add`}
                    element={<GlassesEditorPage />}
                  />
                  <Route
                    path={`${ProductCategoriesLink.GLASSES}/edit`}
                    element={<GlassesEditorPage />}
                  />
                </Route>
                <Route path="/activate-account" element={<ActivateAccount />} />
              </Route>
              <Route path="/user-settings/*" element={<UserSettingsPage />} />
            </Route>
            <Route element={<LoginPagesLayout />}>
              <Route path="/login/sso-callback" element={<LoginSsoCallback />} />
              <Route path="/login" element={<LoginPage />} />
            </Route>
          </Routes>
        </Suspense>
      </AlertProvider>
    </BrowserRouter>
  )
}

export default App
